import {
  AUTOMATIC_FLAT_RESPONSE_PARAMETER,
  PAGE_PARAMETER,
  SEMANTIC_RELEVANCE_THRESHOLD,
  VQL_PARAMETER,
} from "helpers/constants/miscellaneous";
import { setShowQueryPanel } from "redux/SingleDataset/actions";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useCallback } from "react";
import {
  APIQueryOperators,
  FilterKey,
  FilterSubType,
  Filters,
  FilterType,
  Query,
  QueryImage,
  QueryOperator,
  QueryPanelLogic,
  VqlQuery,
  VqlQueryBase,
  isFilterSubType,
} from "types";
import { isVqlQuery } from "helpers/utility/vqlHelpers";
import { useFeatureFlagService } from "./useFeatureFlagService";
import { VQL_UPGRADE } from "../helpers/constants/filters";
import { useUserTagsData } from "views/pages/DataPage/CardsView/DataPageFilters/UserTagsQueryModal/useUserTagsData";

export interface VqlParamsHook {
  vqlQueries: VqlQuery[];
  findVqlQuery: <K extends FilterKey>(
    id: string,
    key: K,
  ) => (VqlQueryBase & Extract<FilterType, Record<K, any>>) | null;
  setVqlQueries: (queries: VqlQuery[], customPathname?: string) => void;
  addVqlQuery: (query: VqlQuery, customPathname?: string) => void;
  removeVqlQuery: (queryId: string) => void;
  updateVqlQuery: (newQuery: VqlQuery) => void;
  vqlToQuery: (
    vqlQuery: VqlQuery,
    imageDataMap?: Record<string, any>,
  ) => Query | null;
}

export type VqlListMutators = Pick<
  VqlParamsHook,
  "addVqlQuery" | "updateVqlQuery" | "removeVqlQuery"
>;

const useVqlParams = (): VqlParamsHook => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isVqlUpgradeEnabled = useFeatureFlagService(VQL_UPGRADE).enabled;
  const getVqlQueries = useCallback((): VqlQuery[] => {
    const searchParams = new URLSearchParams(location.search);
    const vqlParam = searchParams.get(VQL_PARAMETER);
    if (!vqlParam) return [];
    try {
      return JSON.parse(vqlParam) as VqlQuery[];
    } catch {
      return [];
    }
  }, [location.search]);

  const vqlQueries = getVqlQueries();

  const hasTagsQuery = vqlQueries.some((query) => isVqlQuery(query, "tags"));
  const { userTags } = useUserTagsData(hasTagsQuery);

  const setVqlQueries = (queries: VqlQuery[], customPathname?: string) => {
    const baseSearchParams =
      customPathname && customPathname?.includes("?")
        ? customPathname.split("?")[1]
        : location.search.slice(1); // Remove the '?' prefix
    const newSearchParams = new URLSearchParams(baseSearchParams);
    if (queries.length === 0) {
      newSearchParams.delete(VQL_PARAMETER);
      newSearchParams.delete(AUTOMATIC_FLAT_RESPONSE_PARAMETER);
      newSearchParams.delete(SEMANTIC_RELEVANCE_THRESHOLD);
    } else {
      newSearchParams.set(PAGE_PARAMETER, "1");
      newSearchParams.set(VQL_PARAMETER, JSON.stringify(queries));
    }

    const finalSearchString = newSearchParams.toString();

    if (customPathname) {
      const fullUrl = `${customPathname.split("?")[0]}?${finalSearchString}`;
      navigate(fullUrl);
    } else {
      navigate({ search: finalSearchString });
    }
  };

  const mixMatchingQuery = (
    existingQuery: VqlQuery,
    newQuery: VqlQuery,
  ): VqlQuery | null => {
    if (
      isVqlQuery(existingQuery, "labels") &&
      isVqlQuery(newQuery, "labels") &&
      existingQuery.labels.op === newQuery.labels.op
    ) {
      const resultQuery = { ...existingQuery };
      resultQuery.labels.value = Array.from(
        new Set([...existingQuery.labels.value, ...newQuery.labels.value]),
      );
      return resultQuery;
    }

    if (
      isVqlQuery(existingQuery, "issues") &&
      isVqlQuery(newQuery, "issues") &&
      existingQuery.issues.value === newQuery.issues.value &&
      existingQuery.issues.mode === newQuery.issues.mode
    ) {
      return newQuery;
    }

    if (
      isVqlQuery(existingQuery, "tags") &&
      isVqlQuery(newQuery, "tags") &&
      existingQuery.tags.op === newQuery.tags.op
    ) {
      const resultQuery = { ...existingQuery };
      resultQuery.tags.value = Array.from(
        new Set([...existingQuery.tags.value, ...newQuery.tags.value]),
      );
      return resultQuery;
    }

    if (
      isVqlQuery(existingQuery, "custom_metadata") &&
      isVqlQuery(newQuery, "custom_metadata") &&
      existingQuery.custom_metadata.op === newQuery.custom_metadata.op &&
      existingQuery.custom_metadata.field_id ===
        newQuery.custom_metadata.field_id
    ) {
      const resultQuery = { ...existingQuery };
      resultQuery.custom_metadata.value = newQuery.custom_metadata.value;
      return resultQuery;
    }

    if (
      isVqlQuery(existingQuery, "text") &&
      isVqlQuery(newQuery, "text") &&
      existingQuery.text.op === newQuery.text.op
    ) {
      if (isVqlUpgradeEnabled && existingQuery.text.op === "semantic") {
        if (existingQuery.text.value === newQuery.text.value) {
          return {
            ...newQuery,
            text: {
              ...newQuery.text,
              ...(existingQuery.text.negation !== undefined && {
                negation: existingQuery.text.negation,
              }),
              ...(existingQuery.text["groupId"] && {
                groupId: existingQuery.text["groupId"],
              }),
            },
          };
        }
        return null;
      }
      return newQuery;
    }

    if (
      isVqlQuery(existingQuery, "duplicates") &&
      isVqlQuery(newQuery, "duplicates") &&
      existingQuery.duplicates.op === newQuery.duplicates.op
    ) {
      return newQuery;
    }

    if (
      isVqlQuery(existingQuery, "uniqueness") &&
      isVqlQuery(newQuery, "uniqueness")
    ) {
      return newQuery;
    }
    if (
      isVqlQuery(existingQuery, "file") &&
      isVqlQuery(newQuery, "file") &&
      existingQuery.file.op === newQuery.file.op
    ) {
      const resultQuery = { ...existingQuery };
      const existingValues = Array.isArray(existingQuery.file.value)
        ? existingQuery.file.value
        : [existingQuery.file.value];
      const newValues = Array.isArray(newQuery.file.value)
        ? newQuery.file.value
        : [newQuery.file.value];

      resultQuery.file.value = Array.from(
        new Set([...existingValues, ...newValues]),
      );
      return resultQuery;
    }

    if (
      isVqlQuery(existingQuery, "folder") &&
      isVqlQuery(newQuery, "folder") &&
      existingQuery.folder.op === newQuery.folder.op
    ) {
      const resultQuery = { ...existingQuery };
      const existingValues = Array.isArray(existingQuery.folder.value)
        ? existingQuery.folder.value
        : [existingQuery.folder.value];
      const newValues = Array.isArray(newQuery.folder.value)
        ? newQuery.folder.value
        : [newQuery.folder.value];

      resultQuery.folder.value = Array.from(
        new Set([...existingValues, ...newValues]),
      );
      return resultQuery;
    }

    if (
      isVqlQuery(existingQuery, "similarity") &&
      isVqlQuery(newQuery, "similarity")
    ) {
      if (existingQuery.similarity.value === newQuery.similarity.value) {
        return existingQuery;
      } else {
        return null;
      }
    }

    if (
      isVqlQuery(existingQuery, "created_at") &&
      isVqlQuery(newQuery, "created_at") &&
      existingQuery.created_at.op === newQuery.created_at.op
    ) {
      const newVal = newQuery.created_at.value;

      const resultQuery = { ...existingQuery };
      resultQuery.created_at.value = newVal;
      return resultQuery;
    }

    return null;
  };

  const addVqlQuery = (query: VqlQuery, customPathname?: string) => {
    const allQueries = getVqlQueries();
    let mixedQuery: VqlQuery | null = null;
    let existingQueryIndex: number = -1;
    for (let i = 0; i < allQueries.length; i++) {
      mixedQuery = mixMatchingQuery(allQueries[i], query);
      if (mixedQuery !== null) {
        existingQueryIndex = i;
        break;
      }
    }
    if (existingQueryIndex !== -1 && mixedQuery !== null) {
      const updatedQueries = [...allQueries];
      updatedQueries[existingQueryIndex] = mixedQuery;
      setVqlQueries(updatedQueries, customPathname);
    } else {
      setVqlQueries([...allQueries, query], customPathname);
    }
    dispatch(setShowQueryPanel(true));
  };

  const removeVqlQuery = (queryId: string) => {
    const currentQueries = getVqlQueries();
    setVqlQueries(
      currentQueries.filter((query: VqlQuery) => query.id !== queryId),
    );
  };

  const updateVqlQuery = (newQuery: VqlQuery) => {
    const currentQueries = getVqlQueries();

    // Find another existing query that can be merged with the new one
    const matchingIndex = currentQueries.findIndex((query) => {
      if (query.id === newQuery.id) return false;
      return mixMatchingQuery(query, newQuery) !== null;
    });

    // If a matching query exists, merge it
    if (matchingIndex !== -1) {
      const matchingQuery = currentQueries[matchingIndex];
      const mergedQuery = mixMatchingQuery(matchingQuery, newQuery);

      if (mergedQuery) {
        mergedQuery.id = newQuery.id; // keep the original ID being updated

        const updatedQueries = currentQueries
          .filter((q) => q.id !== matchingQuery.id) // remove matching query
          .map((q) => (q.id === newQuery.id ? mergedQuery : q)); // update target

        setVqlQueries(updatedQueries);
        return;
      }
    }

    // No matching query, update normally
    setVqlQueries(
      currentQueries.map((query: VqlQuery) =>
        query.id === newQuery.id ? newQuery : query,
      ),
    );
  };

  const findVqlQuery = <K extends FilterKey>(
    id: string,
    key: K,
  ): (VqlQueryBase & Extract<FilterType, Record<K, any>>) | null => {
    const queries = getVqlQueries();
    const matchingQuery = queries.find((query) => query.id === id);

    // If the query is found, assert the type
    if (matchingQuery && isVqlQuery(matchingQuery, key)) {
      return matchingQuery as VqlQueryBase &
        Extract<FilterType, Record<K, any>>;
    }

    // Return null if not found or the key doesn't match
    return null;
  };

  const vqlToQuery = (
    vqlQuery: VqlQuery,
    imageDataMap?: Record<string, any>,
  ): Query | null => {
    const mapOperator = (op: APIQueryOperators): QueryOperator => {
      const operatorMap: Record<APIQueryOperators, QueryOperator> = {
        one_of: QueryOperator.IS_ONE_OF,
        not_one_of: QueryOperator.IS_NOT_ONE_OF,
        is: QueryOperator.IS,
        is_not: QueryOperator.IS_NOT,
        contains: QueryOperator.CONTAINS,
        starts_with: QueryOperator.STARTS_WITH,
        ends_with: QueryOperator.ENDS_WITH,
        fts: QueryOperator.IS,
        semantic: QueryOperator.IS,
        media: QueryOperator.IS,
        upload: QueryOperator.IS,
        issues: QueryOperator.IS,
        in: QueryOperator.IS,
        out: QueryOperator.IS_NOT,
        duplicates: QueryOperator.IS,
        uniqueness: QueryOperator.IS,
        eq: QueryOperator.IS,
        neq: QueryOperator.IS_NOT,
        gt: QueryOperator.GREATER_THAN,
        lt: QueryOperator.LESS_THAN,
        gte: QueryOperator.GREATER_THAN_OR_EQUAL_TO,
        lte: QueryOperator.LESS_THAN_OR_EQUAL_TO,
        between: QueryOperator.BETWEEN,
        does_not_contain: QueryOperator.DOES_NOT_CONTAIN,
      };
      return operatorMap[op];
    };

    if ("labels" in vqlQuery && vqlQuery.labels) {
      return {
        id: vqlQuery.id,
        type: Filters.LABELS,
        operator: mapOperator(vqlQuery.labels.op),
        values: vqlQuery.labels.value,
        logic: QueryPanelLogic.AND,
      };
    }

    if ("issues" in vqlQuery && vqlQuery.issues) {
      const separatedIssues = ["mislabels", "outliers", "label_outlier"];
      const subType = vqlQuery.issues.value.toUpperCase() as FilterSubType;
      const showsSeparately = separatedIssues.includes(vqlQuery.issues.value);

      const type =
        separatedIssues[0] === vqlQuery.issues.value
          ? Filters.MISLABELS
          : separatedIssues[1] === vqlQuery.issues.value
            ? Filters.OUTLIERS
            : separatedIssues[2] === vqlQuery.issues.value
              ? Filters.LABEL_OUTLIER
              : Filters.ISSUES;

      return {
        type,
        id: vqlQuery.id,
        operator: mapOperator(vqlQuery.issues.mode),
        values:
          isFilterSubType(subType) || showsSeparately
            ? [
                vqlQuery.issues.confidence_min.toString(),
                vqlQuery.issues.confidence_max.toString(),
              ]
            : [vqlQuery.issues.value],
        logic: QueryPanelLogic.AND,
        subType: isFilterSubType(subType) ? subType : undefined,
      };
    }

    if ("tags" in vqlQuery && vqlQuery.tags) {
      return {
        id: vqlQuery.id,
        type: Filters.USER_TAGS,
        operator: mapOperator(vqlQuery.tags.op),
        values: userTags
          .filter((tag) => vqlQuery.tags.value.includes(tag.id.toString()))
          .map((tag) => tag.text),
        logic: QueryPanelLogic.AND,
      };
    }

    if ("file" in vqlQuery && vqlQuery.file) {
      return {
        id: vqlQuery.id,
        type: Filters.FILE,
        operator: mapOperator(vqlQuery.file.op),
        values: Array.isArray(vqlQuery.file.value)
          ? vqlQuery.file.value
          : [vqlQuery.file.value],
        logic: QueryPanelLogic.AND,
      };
    }

    if ("folder" in vqlQuery && vqlQuery.folder) {
      return {
        id: vqlQuery.id,
        type: Filters.FOLDER,
        operator: mapOperator(vqlQuery.folder.op),
        values: Array.isArray(vqlQuery.folder.value)
          ? vqlQuery.folder.value
          : [vqlQuery.folder.value],
        logic: QueryPanelLogic.AND,
      };
    }

    if ("text" in vqlQuery && vqlQuery.text && vqlQuery.text.op === "fts") {
      return {
        id: vqlQuery.id,
        type: Filters.CAPTION,
        operator: mapOperator(vqlQuery.text.op),
        values: [vqlQuery.text.value],
        logic: QueryPanelLogic.AND,
      };
    }

    if (isVqlUpgradeEnabled) {
      if (
        "text" in vqlQuery &&
        vqlQuery.text &&
        vqlQuery.text.op === "semantic"
      ) {
        return {
          id: vqlQuery.id,
          type: Filters.SEMANTIC,
          operator: mapOperator(vqlQuery.text.op),
          values: [vqlQuery.text.value],
          logic: QueryPanelLogic.AND,
          negation: vqlQuery.text.negation ?? false,
          groupId: vqlQuery.text.groupId,
        };
      }

      if (
        "similarity" in vqlQuery &&
        vqlQuery.similarity &&
        (vqlQuery.similarity.op === "media" ||
          vqlQuery.similarity.op === "upload")
      ) {
        const baseQuery = {
          id: vqlQuery.id,
          type: Filters.VISUAL_SIMILARITY,
          operator: mapOperator(vqlQuery.similarity.op),
          negation: vqlQuery.similarity.negation,
          values: [vqlQuery.similarity.value],
          logic: QueryPanelLogic.AND,
          groupId: vqlQuery.similarity.groupId,
        };
        if (imageDataMap) {
          const mediaId = vqlQuery.similarity.value;
          const imageUri = imageDataMap[mediaId]?.image_uri;
          if (imageUri) {
            const image: QueryImage = {
              uri: imageUri,
              boundingBox: vqlQuery.similarity.bounding_box,
            };
            return {
              ...baseQuery,
              image,
            };
          }
        }
        return baseQuery;
      }
    }

    if ("duplicates" in vqlQuery && vqlQuery.duplicates) {
      return {
        id: vqlQuery.id,
        type: Filters.DUPLICATES,
        operator: mapOperator(vqlQuery.duplicates.op),
        values: [vqlQuery.duplicates.value.toString()],
        logic: QueryPanelLogic.AND,
      };
    }

    if ("uniqueness" in vqlQuery && vqlQuery.uniqueness) {
      return {
        id: vqlQuery.id,
        type: Filters.UNIQUENESS,
        operator: mapOperator(vqlQuery.uniqueness.op),
        values: [vqlQuery.uniqueness.value.toString()],
        logic: QueryPanelLogic.AND,
      };
    }

    if ("custom_metadata" in vqlQuery && vqlQuery.custom_metadata) {
      const formatValue = (value: any): string[] => {
        if (Array.isArray(value)) {
          return value.map(String);
        }
        return [String(value)];
      };

      return {
        id: vqlQuery.id,
        type: vqlQuery.custom_metadata,
        operator: mapOperator(vqlQuery.custom_metadata.op),
        values: formatValue(vqlQuery.custom_metadata.value),
        logic: QueryPanelLogic.AND,
      };
    }

    if ("size" in vqlQuery && vqlQuery.size) {
      return {
        id: vqlQuery.id,
        type: Filters.SIZE,
        operator: mapOperator(vqlQuery.size.op),
        values: [vqlQuery.size.value.toString()],
        logic: QueryPanelLogic.AND,
      };
    }

    if ("created_at" in vqlQuery && vqlQuery.created_at) {
      const rawValue = vqlQuery.created_at.value;
      const values = Array.isArray(rawValue)
        ? rawValue.map((v) => String(v))
        : [String(rawValue)];
      return {
        id: vqlQuery.id,
        type: Filters.CREATED_AT,
        operator: mapOperator(vqlQuery.created_at.op),
        values,
        logic: QueryPanelLogic.AND,
      };
    }

    if ("media_status" in vqlQuery && vqlQuery.media_status) {
      return {
        id: vqlQuery.id,
        type: Filters.MEDIA_STATUS,
        operator: mapOperator(vqlQuery.media_status.op),
        values: [vqlQuery.media_status.value.toString()],
        logic: QueryPanelLogic.AND,
      };
    }

    return null;
  };

  return {
    vqlQueries,
    findVqlQuery,
    setVqlQueries,
    addVqlQuery,
    removeVqlQuery,
    updateVqlQuery,
    vqlToQuery,
  };
};

export default useVqlParams;
